// Mapa de specs para fichas técnicas y catálogos NULED.
// Normaliza llaves duplicadas del catálogo (ej. tipo_luminaria vs tipo_de_luminaria),
// define etiquetas en español, orden de despliegue e íconos, y separa
// specs de PRODUCTO (comunes a todas las variantes) de los atributos que
// VARÍAN por variante (acabado, temperatura de color, ángulo, empaque).

// --- Normalización de llaves ---
// Muchas cargas históricas dejaron llaves equivalentes con nombres distintos.
// Canonizamos a una sola llave para no duplicar renglones en la ficha.
const KEY_ALIASES: Record<string, string> = {
  tipo_de_luminaria: 'tipo_luminaria',
  tipo_luminario: 'tipo_luminaria',
  temperatura: 'temperatura_de_color',
  ficha_tecnica: 'fichatecnica',
  tipo_de_riel: 'tipo_riel_magnetico',
};

export function canonicalSpecKey(key: string): string {
  const k = key.trim().toLowerCase();
  return KEY_ALIASES[k] ?? k;
}

// --- Etiquetas, orden e íconos ---
// order: menor = arriba. Las llaves sin definir caen al final ordenadas alfabéticamente.
type SpecMeta = { label: string; order: number; icon?: string };

const SPEC_META: Record<string, SpecMeta> = {
  tipo_luminaria:       { label: 'Tipo de luminaria',    order: 10, icon: '💡' },
  tipo_led:             { label: 'Tipo de LED',          order: 20, icon: '🔆' },
  potencia:             { label: 'Potencia',             order: 30, icon: '⚡' },
  voltaje:              { label: 'Voltaje',              order: 40, icon: '🔌' },
  flujo_luminoso:       { label: 'Flujo luminoso',       order: 50, icon: '☀️' },
  temperatura_de_color: { label: 'Temperatura de color', order: 60, icon: '🌡️' },
  cri:                  { label: 'CRI',                  order: 70, icon: '🎨' },
  angulo:               { label: 'Ángulo de apertura',   order: 80, icon: '📐' },
  sistema_control:       { label: 'Sistema de control',      order: 85 },
  ip:                   { label: 'Grado de protección',  order: 90, icon: '🛡️' },
  leds_m:               { label: 'LEDs por metro',       order: 100 },
  material:             { label: 'Material',             order: 110 },
  acabado:              { label: 'Acabado',              order: 120 },
  base:                 { label: 'Base / casquillo',     order: 130 },
  tipo_riel_magnetico:  { label: 'Tipo de riel',         order: 140 },
  accesorio:            { label: 'Accesorio',            order: 150 },
  dimensiones:          { label: 'Dimensiones',          order: 160, icon: '📏' },
  tiempo_de_entrega:    { label: 'Tiempo de entrega',    order: 900 },
  // fichatecnica y tipo se manejan aparte (no se pintan como spec normal)
};

// Llaves que NO se muestran como renglón de spec (metadata / referencias).
// Los identificadores del proveedor son información operativa: permanecen en
// administración y compras, pero nunca deben publicarse en catálogo o fichas.
const HIDDEN_SPEC_KEYS = new Set([
  'fichatecnica',
  'tipo',
  'modelo_proveedor',
  'modelo_del_proveedor',
  'supplier_model',
  'supplier_sku',
  'sku_proveedor',
  'codigo_proveedor',
  'clave_proveedor',
  'referencia_proveedor',
  'numero_parte_proveedor',
  'numero_de_parte_del_proveedor',
  'supplier_part_number',
  'manufacturer_model',
  'manufacturer_sku',
  'modelo_fabricante',
  'sku_fabricante',
  'codigo_fabricante',
  'sku_etiqueta',
  'codigo_etiqueta',
]);

export function isPublicSpecKey(rawKey: string): boolean {
  const normalized = canonicalSpecKey(rawKey)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return !HIDDEN_SPEC_KEYS.has(normalized);
}

// Sufijo de unidad sugerido cuando el valor viene "pelón" (solo número).
const SPEC_UNIT: Record<string, string> = {
  potencia: 'W',
  voltaje: 'V',
  temperatura_de_color: 'K',
  angulo: '°',
  flujo_luminoso: 'lm',
};

export type SpecRow = { key: string; label: string; value: string; icon?: string };

function prettyValue(key: string, raw: unknown): string {
  let v = String(raw ?? '').trim();
  if (!v) return '';
  const unit = SPEC_UNIT[key];
  // Si hay unidad conocida y el valor es puramente numérico, la añadimos.
  if (unit && /^[\d.,]+$/.test(v)) v = `${v} ${unit}`;
  // IP suele venir como "20" → "IP20".
  if (key === 'ip' && /^\d+$/.test(v)) v = `IP${v}`;
  // CRI suele venir como "90" → ">90".
  if (key === 'cri' && /^\d+$/.test(v)) v = `Ra ${v}`;
  return v;
}

// Convierte el jsonb specs de un producto en renglones ordenados y limpios,
// omitiendo llaves ocultas, vacías o duplicadas.
export function productSpecRows(specs: unknown): SpecRow[] {
  if (!specs || typeof specs !== 'object') return [];
  const merged: Record<string, string> = {};
  for (const rawKey of Object.keys(specs as Record<string, unknown>)) {
    const key = canonicalSpecKey(rawKey);
    if (!isPublicSpecKey(key)) continue;
    const value = prettyValue(key, (specs as Record<string, unknown>)[rawKey]);
    if (!value) continue;
    // No sobreescribir si ya hay valor (primer valor gana).
    if (!merged[key]) merged[key] = value;
  }
  const rows: SpecRow[] = Object.keys(merged).map(key => {
    const meta = SPEC_META[key];
    return {
      key,
      label: meta?.label ?? humanize(key),
      value: merged[key],
      icon: meta?.icon,
    };
  });
  rows.sort((a, b) => {
    const oa = SPEC_META[a.key]?.order ?? 500;
    const ob = SPEC_META[b.key]?.order ?? 500;
    if (oa !== ob) return oa - ob;
    return a.label.localeCompare(b.label, 'es');
  });
  return rows;
}

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// --- Variantes ---
// Detecta qué dimensiones distinguen a las variantes de un producto, para
// pintar una matriz compacta en vez de N renglones idénticos.

export type VariantLike = {
  full_sku: string;
  sku_suffix: string | null;
  finish: string | null;
  color_temperature_k: number | null;
  beam_angle_deg: number | null;
  wattage_w: number | null;
  ip_rating: string | null;
  voltage_v: number | null;
  dim_method: string | null;
  attributes: Record<string, unknown> | null;
};

export type VariantColumn = { key: string; label: string; get: (v: VariantLike) => string };

// Columnas candidatas para la matriz de variantes, en orden de prioridad.
const VARIANT_COLUMNS: VariantColumn[] = [
  { key: 'finish',              label: 'Acabado',     get: v => v.finish ?? '' },
  { key: 'dim_method',          label: 'Control',     get: v => v.dim_method ?? '' },
  { key: 'beam_angle_deg',      label: 'Óptica',      get: v => v.beam_angle_deg != null ? `${v.beam_angle_deg}°` : '' },
  { key: 'rango_optico',        label: 'Óptica',      get: v => attrStr(v, 'rango_optico') },
  { key: 'color_temperature_k', label: 'Temp. color', get: v => v.color_temperature_k != null ? `${v.color_temperature_k}K` : '' },
  { key: 'wattage_w',           label: 'Potencia',    get: v => v.wattage_w != null ? `${v.wattage_w} W` : '' },
  { key: 'ip_rating',           label: 'IP',          get: v => v.ip_rating ?? '' },
  { key: 'voltage_v',           label: 'Voltaje',     get: v => v.voltage_v != null ? `${v.voltage_v} V` : '' },
  { key: 'packaging',           label: 'Empaque',     get: v => attrStr(v, 'packaging') },
];

function attrStr(v: VariantLike, name: string): string {
  const a = v.attributes;
  if (!a || typeof a !== 'object') return '';
  const val = (a as Record<string, unknown>)[name];
  return val != null ? String(val) : '';
}

// Devuelve solo las columnas que realmente VARÍAN entre las variantes dadas
// (más de un valor distinto) — esas son las que definen a la variante.
export function distinguishingColumns(variants: VariantLike[]): VariantColumn[] {
  if (variants.length <= 1) return [];
  const cols: VariantColumn[] = [];
  for (const col of VARIANT_COLUMNS) {
    const seen = new Set<string>();
    for (const v of variants) {
      const val = col.get(v);
      if (val) seen.add(val);
    }
    if (seen.size > 1) cols.push(col);
  }
  return cols;
}

// La ficha debe documentar los ejes de configuración aunque alguno tenga una
// sola opción. La potencia no es un eje; si cambia como consecuencia del
// control seleccionado, se agrega únicamente como dato informativo de la fila.
export function configurationColumns(variants: VariantLike[]): VariantColumn[] {
  const axisKeys = new Set(['finish', 'dim_method', 'beam_angle_deg', 'rango_optico', 'color_temperature_k']);
  const wattages = new Set(variants.map(v => v.wattage_w).filter(v => v != null).map(String));
  return VARIANT_COLUMNS.filter(col =>
    (axisKeys.has(col.key) && variants.some(v => Boolean(col.get(v))))
    || (col.key === 'wattage_w' && wattages.size > 1)
  );
}

// Valores comunes a TODAS las variantes (no varían) → suben a specs del producto.
export function commonVariantSpecs(variants: VariantLike[]): SpecRow[] {
  if (variants.length === 0) return [];
  const rows: SpecRow[] = [];
  for (const col of VARIANT_COLUMNS) {
    if (col.key === 'packaging' || col.key === 'finish') continue; // cosméticos, no specs técnicas comunes
    const vals = new Set<string>();
    for (const v of variants) {
      const val = col.get(v);
      if (val) vals.add(val);
    }
    if (vals.size === 1) {
      const only = Array.from(vals)[0];
      const specKey = ({
        color_temperature_k: 'temperatura_de_color',
        wattage_w: 'potencia',
        beam_angle_deg: 'angulo',
        rango_optico: 'angulo',
        dim_method: 'sistema_control',
        ip_rating: 'ip',
        voltage_v: 'voltaje',
      } as Record<string, string>)[col.key] ?? col.key;
      const meta = SPEC_META[specKey];
      rows.push({ key: specKey, label: meta?.label ?? col.label, value: only, icon: meta?.icon });
    }
  }
  return rows;
}
